import type { AusResult, ProviderConfig, ProviderId } from '../types/aus'

export const providers: Record<ProviderId, ProviderConfig> = {
  fannie: {
    id: 'fannie',
    name: 'Fannie Mae',
    product: 'Desktop Underwriter',
    shortLabel: 'DU / DO',
    description:
      'Run Fannie Mae underwriting findings using the loan and credit information already on file.',
    runLabel: 'Run Fannie Mae',
    submitLabel: 'Submit to Fannie Mae',
    processingLabel: 'Submitting to Fannie Mae Desktop Underwriter…',
    accentText: 'text-brand-700',
    accentBg: 'bg-brand-50',
    accentBorder: 'border-brand-300',
    accentSolid: 'bg-brand-700',
    accentSolidHover: 'hover:bg-brand-800',
    accentRing: 'focus-visible:ring-brand-700',
  },
  freddie: {
    id: 'freddie',
    name: 'Freddie Mac',
    product: 'Loan Product Advisor',
    shortLabel: 'LPA',
    description:
      'Run Freddie Mac underwriting findings using the loan and credit information already on file.',
    runLabel: 'Run Freddie Mac',
    submitLabel: 'Submit to Freddie Mac',
    processingLabel: 'Submitting to Freddie Mac Loan Product Advisor…',
    accentText: 'text-graphite-700',
    accentBg: 'bg-graphite-100',
    accentBorder: 'border-graphite-300',
    accentSolid: 'bg-graphite-700',
    accentSolidHover: 'hover:bg-graphite-800',
    accentRing: 'focus-visible:ring-graphite-700',
  },
}

export const providerResults: Record<ProviderId, AusResult> = {
  fannie: {
    provider: 'fannie',
    recommendation: 'Approve / Eligible',
    eligibility: 'Eligible for delivery to Fannie Mae',
    riskAssessment: 'DU Version 11.1 · Risk assessed by Desktop Underwriter',
    submissionId: 'SUB-88213-041',
    caseLabel: 'DU Casefile ID',
    caseNumber: '2608D-9931204',
    submittedAt: 'Aug 18, 2026 9:07 AM',
    findings: [
      {
        id: 'f-1',
        severity: 'success',
        category: 'Recommendation',
        title: 'Approve / Eligible',
        description: 'The loan casefile meets Fannie Mae eligibility requirements.',
        detail:
          'Desktop Underwriter evaluated the credit, capacity, and collateral data on this loan casefile and returned an Approve / Eligible recommendation. No additional risk-based documentation is required beyond the verification messages listed below.',
      },
      {
        id: 'f-2',
        severity: 'info',
        category: 'Verification',
        title: 'Income documentation requirements',
        description: 'Provide the most recent paystub and one year of W-2s for the borrower.',
        detail:
          'Obtain a paystub dated within 30 days of the note date covering a minimum of 30 days of year-to-date earnings, plus IRS W-2 forms for the most recent tax year for Jody Sanders.',
      },
      {
        id: 'f-3',
        severity: 'info',
        category: 'Collateral',
        title: 'Appraisal waiver considered',
        description: 'This casefile is eligible for a value acceptance offer.',
        detail:
          'Value acceptance (appraisal waiver) was offered for the subject property. If the offer is exercised, the loan must be delivered with Special Feature Code 801.',
      },
      {
        id: 'f-4',
        severity: 'attention',
        category: 'Credit',
        title: 'Verify recent credit inquiry',
        description: 'One inquiry within the last 90 days requires a borrower explanation.',
        detail:
          'A credit inquiry dated Jul 29, 2026 appears on the merged report. Obtain a written explanation confirming whether new debt was opened, and include any resulting monthly obligation in the qualifying ratios.',
      },
      {
        id: 'f-5',
        severity: 'info',
        category: 'Observation',
        title: 'Qualifying ratios within tolerance',
        description: 'Total debt-to-income ratio of 34.812% was used in this assessment.',
        detail:
          'Housing expense ratio 24.106% and total expense ratio 34.812% were calculated using $7,450.00 of qualifying monthly income.',
      },
    ],
  },
  freddie: {
    provider: 'freddie',
    recommendation: 'Accept',
    eligibility: 'Eligible for purchase by Freddie Mac',
    riskAssessment: 'LPA Version 5.4.06 · Risk Class: Accept',
    submissionId: 'SUB-88214-007',
    caseLabel: 'LPA Key Number',
    caseNumber: 'A20-4471928',
    submittedAt: 'Aug 18, 2026 9:11 AM',
    findings: [
      {
        id: 'f-1',
        severity: 'success',
        category: 'Risk Class',
        title: 'Accept',
        description: 'Loan Product Advisor returned an Accept risk class for this loan.',
        detail:
          'The loan received a risk class of Accept and a purchase eligibility of Eligible. Represent and warrant relief applies to the credit reputation and capacity data assessed by Loan Product Advisor.',
      },
      {
        id: 'f-2',
        severity: 'success',
        category: 'Relief',
        title: 'Asset representation relief granted',
        description: 'Automated collateral evaluation was accepted for the subject property.',
        detail:
          'Freddie Mac granted relief from representations and warranties related to the value, condition, and marketability of the subject property based on automated collateral evaluation.',
      },
      {
        id: 'f-3',
        severity: 'info',
        category: 'Documentation',
        title: 'Income and employment verification',
        description: 'Streamlined documentation applies to the borrower’s salaried income.',
        detail:
          'Provide one paystub covering 30 days of year-to-date earnings and verbal verification of employment completed within 10 business days of the note date.',
      },
      {
        id: 'f-4',
        severity: 'attention',
        category: 'Property',
        title: 'Confirm flood zone determination',
        description: 'A flood certification is required prior to closing.',
        detail:
          'The subject property is located in Effingham County, GA. Obtain a life-of-loan flood zone determination and, if the property is in a Special Flood Hazard Area, evidence of adequate flood insurance coverage.',
      },
    ],
  },
}
